import type { RemoteTrack, Room, TrackPublication } from "livekit-client";
import { useEffect, useRef, useState } from "react";
import { say } from "../localization/say.ts";
import { act } from "../net/rally.ts";
import { afterMediaToggle, noteTheMic } from "./media-state.ts";

export type Speaker = {
  identity: string;
  video: MediaStreamTrack | null;

  audio: MediaStreamTrack | null;
  muted: boolean;
  speaking: boolean;
};

export type Wired = {
  speakers: Speaker[];
  wired: boolean;
  mic: boolean;
  camera: boolean;
  toggleMic: () => void;
  toggleCamera: () => void;

  broke: string | null;
};

type MediaRoom = Pick<Room, "disconnect"> & {
  localParticipant: Pick<Room["localParticipant"], "getTrackPublications">;
};

export async function disconnectMediaRoom(held: MediaRoom): Promise<void> {
  for (const publication of held.localParticipant.getTrackPublications()) publication.track?.stop();
  await held.disconnect(true);
}

export function useMedia(call: string | null, me: string, withCamera = false): Wired {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [wired, setWired] = useState(false);
  const [mic, setMic] = useState(true);
  const [camera, setCamera] = useState(withCamera);
  const [broke, setBroke] = useState<string | null>(null);
  const room = useRef<Room | null>(null);

  const wanted = useRef(withCamera);
  wanted.current = withCamera;

  useEffect(() => {
    if (!call) return;

    setCamera(wanted.current);
    let dropped = false;
    let held: Room | null = null;

    void (async () => {
      try {
        const kit = await import("livekit-client");
        const seat = await act<{ token: string; url: string }>("call.credentials", { call });
        if (dropped) return;
        const room2 = new kit.Room({ adaptiveStream: true, dynacast: true });
        held = room2;
        room.current = room2;

        const readTheRoom = () => {
          const seen: Speaker[] = [];
          const local = room2.localParticipant;

          noteTheMic({ mic: local.isMicrophoneEnabled, speaking: local.isSpeaking });
          seen.push({
            identity: local.identity || me,
            video: mediaOf(local.getTrackPublications(), kit.Track.Source.Camera),
            audio: null,
            muted: !local.isMicrophoneEnabled,
            speaking: local.isSpeaking,
          });
          for (const one of room2.remoteParticipants.values()) {
            seen.push({
              identity: one.identity,
              video: mediaOf(one.getTrackPublications(), kit.Track.Source.Camera),
              audio: mediaOf(one.getTrackPublications(), kit.Track.Source.Microphone),
              muted: !one.isMicrophoneEnabled,
              speaking: one.isSpeaking,
            });
          }
          setSpeakers(seen);
        };

        for (const event of [
          kit.RoomEvent.ParticipantConnected,
          kit.RoomEvent.ParticipantDisconnected,
          kit.RoomEvent.TrackSubscribed,
          kit.RoomEvent.TrackUnsubscribed,
          kit.RoomEvent.TrackMuted,
          kit.RoomEvent.TrackUnmuted,
          kit.RoomEvent.LocalTrackPublished,
          kit.RoomEvent.LocalTrackUnpublished,
          kit.RoomEvent.ActiveSpeakersChanged,
        ]) {
          room2.on(event, readTheRoom);
        }

        room2.on(kit.RoomEvent.Reconnecting, () => setWired(false));
        room2.on(kit.RoomEvent.Reconnected, () => setWired(true));
        room2.on(kit.RoomEvent.Disconnected, () => setWired(false));

        await room2.connect(seat.url, seat.token);
        if (dropped) return;

        void room2.startAudio().catch(() => undefined);
        await room2.localParticipant.setMicrophoneEnabled(true);

        if (wanted.current) await room2.localParticipant.setCameraEnabled(true);
        setWired(true);
        readTheRoom();
      } catch {
        if (!dropped) setBroke(say("call.broke"));
      }
    })();

    return () => {
      dropped = true;
      room.current = null;
      setSpeakers([]);
      setWired(false);
      noteTheMic({ mic: false, speaking: false });
      if (held) void disconnectMediaRoom(held);
    };
  }, [call, me]);

  return {
    speakers,
    wired,
    mic,
    camera,
    broke,
    toggleMic: () => {
      const held = room.current;
      if (!held) return;
      const want = afterMediaToggle({ call: call ?? "", mic, camera }, "mic").mic;
      setMic(want);
      void held.localParticipant.setMicrophoneEnabled(want);
    },
    toggleCamera: () => {
      const held = room.current;
      if (!held) return;
      const want = afterMediaToggle({ call: call ?? "", mic, camera }, "camera").camera;
      setCamera(want);
      void held.localParticipant.setCameraEnabled(want);
    },
  };
}

function mediaOf(published: readonly TrackPublication[], source: string): MediaStreamTrack | null {
  for (const one of published) {
    if (String(one.source) !== String(source)) continue;
    const track = one.track as RemoteTrack | undefined;
    if (track?.mediaStreamTrack) return track.mediaStreamTrack;
  }
  return null;
}
